import { usePrivy } from "@privy-io/react-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { env } from "@/utils/envs";
import QueryKey from "@/utils/queryKeys";

export type PointGrantSource = "trivia" | "other";

export type PointGrantInput = {
	userId: string;
	points: number;
	source: PointGrantSource;
	// Use a stable idempotency key (e.g. trivia-2026-02-16-user123) to prevent duplicate awards on retries.
	sourceRef: string;
	description?: string | null;
	metadata?: Record<string, unknown> | null;
};

export type PointGrantsRequest = {
	grants: PointGrantInput[];
};

export type RewardsGrantUser = {
	id: string;
	displayName: string | null;
	email: string | null;
	selectedShinkaiIdentity: string | null;
	walletAddress: string | null;
	discordUsername: string | null;
	rewardsEnabled: boolean;
};

export type RewardsUsersResponse = {
	users: RewardsGrantUser[];
	q: string;
	limit: number;
};

export type PointGrantSourceOption = {
	value: string;
	label: string;
	description?: string | null;
	requiredMetadata: string[];
	metadataExample?: Record<string, unknown> | null;
};

const normalizeRewardsUser = (payload: unknown): RewardsGrantUser | null => {
	if (!payload || typeof payload !== "object") {
		return null;
	}

	const entry = payload as Record<string, unknown>;
	const idValue = entry.id ?? entry.userId;
	if (typeof idValue !== "string" || idValue.length === 0) {
		return null;
	}

	return {
		id: idValue,
		displayName:
			typeof entry.displayName === "string" ? entry.displayName : null,
		email: typeof entry.email === "string" ? entry.email : null,
		selectedShinkaiIdentity:
			typeof entry.selectedShinkaiIdentity === "string"
				? entry.selectedShinkaiIdentity
				: null,
		walletAddress:
			typeof entry.walletAddress === "string" ? entry.walletAddress : null,
		discordUsername:
			typeof entry.discordUsername === "string" ? entry.discordUsername : null,
		rewardsEnabled: Boolean(entry.rewardsEnabled),
	};
};

const normalizeRewardsUsersPayload = (
	payload: unknown,
	q: string,
	limit: number,
): RewardsUsersResponse => {
	const source =
		Array.isArray(payload)
			? payload
			: payload && typeof payload === "object"
				? ((payload as Record<string, unknown>).users ??
					(payload as Record<string, unknown>).items ??
					(payload as Record<string, unknown>).results)
				: [];

	const users = Array.isArray(source)
		? source
				.map(normalizeRewardsUser)
				.filter((user): user is RewardsGrantUser => user !== null)
		: [];

	return { users, q, limit };
};

const normalizePointGrantSource = (
	payload: unknown,
): PointGrantSourceOption | null => {
	if (typeof payload === "string") {
		return {
			value: payload,
			label: payload,
			requiredMetadata: [],
		};
	}

	if (!payload || typeof payload !== "object") {
		return null;
	}

	const entry = payload as Record<string, unknown>;
	const valueRaw = entry.value ?? entry.source ?? entry.key ?? entry.name;
	if (typeof valueRaw !== "string" || valueRaw.length === 0) {
		return null;
	}

	const requiredMetadataRaw =
		entry.requiredMetadata ??
		entry.requiredMetadataFields ??
		entry.required ??
		entry.metadataRequiredFields;
	const requiredMetadata = Array.isArray(requiredMetadataRaw)
		? requiredMetadataRaw.filter(
				(field): field is string => typeof field === "string" && field.length > 0,
			)
		: [];

	const metadataExampleRaw =
		entry.metadataExample ??
		entry.example ??
		entry.metadataTemplate ??
		entry.template;
	const metadataExample =
		metadataExampleRaw &&
		typeof metadataExampleRaw === "object" &&
		!Array.isArray(metadataExampleRaw)
			? (metadataExampleRaw as Record<string, unknown>)
			: null;

	return {
		value: valueRaw,
		label:
			typeof entry.label === "string" && entry.label.length > 0
				? entry.label
				: valueRaw,
		description:
			typeof entry.description === "string"
				? entry.description
				: typeof entry.hint === "string"
					? entry.hint
					: null,
		requiredMetadata,
		metadataExample,
	};
};

const normalizePointGrantSourcesPayload = (
	payload: unknown,
): PointGrantSourceOption[] => {
	const source =
		Array.isArray(payload)
			? payload
			: payload && typeof payload === "object"
				? ((payload as Record<string, unknown>).sources ??
					(payload as Record<string, unknown>).items ??
					(payload as Record<string, unknown>).values)
				: [];

	if (!Array.isArray(source)) {
		return [];
	}

	return source
		.map(normalizePointGrantSource)
		.filter((option): option is PointGrantSourceOption => option !== null);
};

export const useRewardsUsers = (options: {
	query: string;
	limit?: number;
	enabled?: boolean;
}) => {
	const { authenticated, ready, getAccessToken } = usePrivy();
	const query = options.query.trim();
	const limit = options.limit ?? 8;

	return useQuery({
		queryKey: [QueryKey.REWARDS_USERS, query, limit],
		queryFn: async (): Promise<RewardsUsersResponse> => {
			const token = await getAccessToken();
			if (!token) {
				throw new Error("Missing access token");
			}

			const response = await fetch(
				`${env.VITE_SHINKAI_WEB_API_URL}/api/rewards/users?q=${encodeURIComponent(
					query,
				)}&limit=${limit}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				throw new Error(`Failed to fetch users (${response.status})`);
			}

			const payload = (await response.json()) as unknown;
			return normalizeRewardsUsersPayload(payload, query, limit);
		},
		enabled:
			(options.enabled ?? true) &&
			ready &&
			authenticated &&
			query.length >= 2,
		staleTime: 20_000,
	});
};

export const usePointGrantSourceOptions = (options?: { enabled?: boolean }) => {
	const { authenticated, ready, getAccessToken } = usePrivy();

	return useQuery({
		queryKey: [QueryKey.POINT_GRANT_SOURCES],
		queryFn: async (): Promise<PointGrantSourceOption[]> => {
			const token = await getAccessToken();
			if (!token) {
				throw new Error("Missing access token");
			}

			const response = await fetch(
				`${env.VITE_SHINKAI_WEB_API_URL}/api/rewards/point-grants`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				throw new Error(
					`Failed to fetch point grant sources (${response.status})`,
				);
			}

			const payload = (await response.json()) as unknown;
			return normalizePointGrantSourcesPayload(payload);
		},
		enabled: (options?.enabled ?? true) && ready && authenticated,
		staleTime: 60_000,
	});
};

export const usePointGrants = () => {
	const { getAccessToken } = usePrivy();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: PointGrantsRequest) => {
			const token = await getAccessToken();
			if (!token) {
				throw new Error("Missing access token");
			}

			const response = await fetch(
				`${env.VITE_SHINKAI_WEB_API_URL}/api/rewards/point-grants`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify(payload),
				},
			);

			if (!response.ok) {
				const errorPayload = (await response.json().catch(() => ({}))) as {
					error?: string;
					message?: string;
				};
				throw new Error(
					errorPayload.error || errorPayload.message || "Request failed",
				);
			}

			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QueryKey.MY_QUESTS] });
			queryClient.invalidateQueries({ queryKey: [QueryKey.POINTS_ACTIVITY] });
			queryClient.invalidateQueries({ queryKey: [QueryKey.POINTS_SUMMARY] });
			queryClient.invalidateQueries({
				queryKey: [QueryKey.POINT_GRANTS_ACTIVITY],
			});
			queryClient.invalidateQueries({
				queryKey: [QueryKey.REWARDS_LEADERBOARD],
			});
		},
	});
};
